// ink-component
import React, { Children, ReactNode } from 'react';
import flattenChildren from 'react-keyed-flatten-children';

import getGlobalProps from '../../helpers/constants/global-props';
import { SpacingType, AlignType, AlignYType } from '../../helpers/constants/layoutPropTypes';

import Frame from '../Frame/Frame';
import StyledBox from '../StyledBox/StyledBox';
import { LayoutHTMLType } from '../_common/Types/CustomTypes';

export interface HStackProps extends React.HTMLProps<LayoutHTMLType> {
  /** Contents. */
  children?: ReactNode;
  /** Set gap spacing between children. Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  spacing?: SpacingType;
  /** Alignment property. May be set as an array for responsive behavior. */
  align?: AlignType | AlignType[];
  /** Vertical alignment property. May be set as an array for responsive behavior. */
  alignY?: AlignYType | AlignYType[];
  /** Modifier for wrapable content. */
  wraps?: boolean;
  /** Sets overflow-x to auto */
  scrollable?: boolean;
  /** Tag that will be rendered by the Component. */
  as?: 'div' | 'ol' | 'ul' | 'span';
  /** Tag that will be rendered by the Component's immediate children. */
  childrenAs?: string;
}

/**
 * @purpose Layout items in a horizontal stack
 * @group Layout
 * @position 2
 * @categories layout
 * @aka flexbox, row
 * @HTMLType LayoutHTMLType
 */
function HStack(props: HStackProps) {
  const {
    className,
    style,
    children,
    spacing,
    align = 'left',
    alignY = 'baseline',
    wraps = false,
    scrollable = false,
    as = 'div',
    childrenAs = 'div',
    ...otherProps
  } = props;

  const resolveAlignment = align => {
    const aligmentMap = {
      left: 'flex-start',
      top: 'flex-start',
      center: 'center',
      baseline: 'baseline',
      right: 'flex-end',
      bottom: 'flex-end',
      distributed: 'space-between',
    };

    if (typeof align === 'string') {
      return aligmentMap[align];
    }
    if (typeof align === 'object') {
      return align.map(item => aligmentMap[item]);
    }
    return 'flex-start';
  };

  const isList = as === 'ul' || as === 'ol';

  const globalProps = getGlobalProps({});

  return (
    <Frame>
      <StyledBox
        otherProps={otherProps}
        component={as}
        display="flex"
        flexWrap={wraps ? 'wrap' : 'nowrap'}
        overflowX={scrollable ? 'auto' : 'visible'}
        justifyContent={resolveAlignment(align)}
        alignItems={resolveAlignment(alignY)}
        negativeMarginTop={spacing}
        negativeMarginLeft={spacing}
        // When align is distributed, we want spacing between children to be greater than or equal to spacing.
        // Because of the negative margin-left treatment of the container StyledBox, we need the following
        // calculated width when align is distributed to compensate the negative margin visual offset.
        // When Safari support the css gap property for display: flex, we should replace HStack's
        // negative margin logic.
        distributedWidth={align === 'distributed' ? spacing : undefined}
        {...globalProps}
      >
        {Children.map(flattenChildren(children), child => (
          <StyledBox mt={spacing} ml={spacing} component={isList ? 'li' : childrenAs}>
            {child}
          </StyledBox>
        ))}
      </StyledBox>
    </Frame>
  );
}

HStack.defaultProps = {
  children: undefined,
  spacing: 'gutter',
  align: 'left',
  alignY: 'baseline',
  wraps: false,
  scrollable: false,
  as: 'div',
  childrenAs: 'div',
};

HStack.displayName = 'HStack';

export default HStack;
